import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const faqData = [
  { q: 'Comment savoir si la sangle de mon volet roulant doit être changée ?', a: 'Les signes sont visibles : sangle effilochée, qui présente des coupures ou des zones d\'usure, sangle qui se bloque en cours de manipulation, ou sangle complètement cassée. Si vous constatez l\'un de ces signes, faites-la remplacer rapidement pour éviter la panne complète.' },
  { q: 'Peut-on passer d\'un volet à sangle à un volet motorisé ?', a: 'Oui, c\'est tout à fait possible. Nous pouvons remplacer votre mécanisme à sangle par une motorisation électrique. Cela implique le remplacement de l\'enrouleur par un moteur et l\'installation d\'un interrupteur ou d\'une télécommande. Nous vous conseillons sur la faisabilité et le coût lors du diagnostic.' },
];

export default function ChangerSangle() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question', name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <>
      <SEOHead
        title="Changement de sangle de volet roulant à Lyon | DFPRO"
        description="Sangle de volet roulant cassée ou usée à Lyon ? Remplacement rapide par un technicien qualifié. Possibilité de passage en motorisation. Devis gratuit."
        canonicalUrl="https://depanfenetres-lyon.fr/changer-sangle-volet-roulant-lyon/"
        jsonLd={faqJsonLd}
      />
      <Header />
      <section className="w-full">
        <img src="/images/dfpro-volet-sangle-2026.jpg" alt="Changement de sangle de volet roulant à Lyon" className="w-full h-[200px] md:h-[300px] object-cover object-center" loading="lazy" />
      </section>


      <main className="pt-20">
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-secondary-400 mb-6 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors">Accueil</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <Link to="/reparation-volet-roulant-lyon/" className="hover:text-primary-500 transition-colors">Volet roulant</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-secondary-500">Changement de sangle</span>
            </nav>
            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-800">Remplacement de sangle de volet roulant à Lyon</h1>
            <p className="mt-4 text-sm md:text-lg text-secondary-500 max-w-3xl leading-relaxed">
              Sangle de volet roulant usée, effilochée ou cassée ? Nos techniciens remplacent votre sangle rapidement à Lyon et dans la métropole.
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Pourquoi et quand changer la sangle de son volet roulant ?</h2>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    La sangle est un élément essentiel du volet roulant manuel. C'est elle qui transmet votre effort de traction au mécanisme d'enroulement situé dans le caisson. Avec le temps, les frottements répétés et l'exposition aux UV (pour la partie visible), la sangle se dégrade progressivement.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Une sangle usée présente des signes caractéristiques : elle s'effiloche sur les bords, des fils blancs apparaissent, sa couleur passe (décoloration due au soleil), elle devient moins souple. Si vous attendez trop, la sangle finira par casser net, généralement au pire moment — volet en position basse, impossible à remonter.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Le remplacement d'une sangle est une intervention rapide (environ 30 à 45 minutes) et peu coûteuse. Nos techniciens se déplacent avec des sangles de différentes largeurs et couleurs pour s'adapter à votre installation existante. Nous vérifions également l'état du mécanisme d'enroulement pour nous assurer que tout fonctionne correctement.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Et si vous passiez à la motorisation ?</h2>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Profiter du changement de sangle pour motoriser votre volet roulant est une excellente idée. Plutôt que de remplacer simplement la sangle, nous pouvons installer un moteur électrique dans le caisson. Vous gagnez en confort : plus besoin de tirer sur la sangle, une simple pression sur un interrupteur ou une télécommande suffit.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Le passage du manuel à l'électrique est une opération que nous réalisons couramment à Lyon. Nous retirons l'ancien mécanisme à sangle, installons un moteur adapté (filaire ou radio) et posons la commande de votre choix. C'est l'occasion de moderniser votre installation tout en résolvant le problème de sangle.
                  </p>
                </div>
              </div>

              <aside className="space-y-6">
                <div className="bg-background-100 rounded-lg p-5 sticky top-24">
                  <h3 className="font-heading font-semibold text-sm text-foreground-800 mb-4">Services connexes</h3>
                  <nav className="space-y-1">
                    <Link to="/manivelle-volet-roulant-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Réparation manivelle</Link>
                    <Link to="/motorisation-volet-roulant-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Motorisation volet roulant</Link>
                    <Link to="/reparation-volet-roulant-lyon/" className="block text-sm text-primary-500 hover:text-primary-600 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors font-medium">← Tous nos services</Link>
                  </nav>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800 text-center mb-8">Questions fréquentes — changement de sangle</h2>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner title="Sangle de volet roulant cassée à Lyon ?" subtitle="Remplacement rapide, toutes largeurs. Devis gratuit." />
      </main>
      <Footer />
    </>
  );
}